/* ============================================
   FOLLICLE & CO. — CART DRAWER
   Shopify Ajax Cart API integration
   ============================================ */

const CartDrawer = (() => {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 10000; // cents — $100
  let drawerEl = null;
  let cartState = null;
  const variantCache = {}; // productHandle -> sorted variants array

  // ---------- Shopify Ajax helpers ----------

  function shopifyFetch(url, options) {
    const defaults = {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };
    return fetch(url, Object.assign(defaults, options)).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + res.status);
      return res.json();
    });
  }

  function fetchCart() {
    return shopifyFetch('/cart.js').then(function (cart) {
      cartState = cart;
      updateNavCounter();
      return cart;
    });
  }

  function addItem(variantId, qty) {
    var quantity = qty || 1;
    return shopifyFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: quantity }],
      }),
    }).then(function () {
      // Attach quiz session ID as cart attribute for order attribution
      var quizSessionId = sessionStorage.getItem('fc_quiz_session_id');
      if (quizSessionId) {
        return shopifyFetch('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({
            attributes: {
              quiz_session_id: quizSessionId,
              quiz_variant: sessionStorage.getItem('fc_quiz_variant') || '',
              landing_source: sessionStorage.getItem('fc_landing_source') || ''
            }
          })
        }).catch(function() {}).then(function() {
          return fetchCart();
        });
      }
      return fetchCart();
    }).then(function (cart) {
      renderDrawer(cart);
      return cart;
    });
  }

  function changeItem(lineItemKey, quantity) {
    return shopifyFetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: lineItemKey,
        quantity: quantity,
      }),
    }).then(function () {
      return fetchCart();
    }).then(function (cart) {
      renderDrawer(cart);
      return cart;
    });
  }

  function removeItem(lineItemKey) {
    return changeItem(lineItemKey, 0);
  }

  // ---------- Variant tier helpers ----------

  function fetchProductVariants(handle) {
    if (variantCache[handle]) {
      return Promise.resolve(variantCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (product) {
        // Sort variants by price ascending
        var sorted = product.variants.slice().sort(function (a, b) {
          return a.price - b.price;
        });
        variantCache[handle] = sorted;
        return sorted;
      });
  }

  function swapVariantTier(lineItemKey, currentVariantId, handle, delta, currentQty) {
    return fetchProductVariants(handle).then(function (variants) {
      var currentIndex = -1;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === currentVariantId) {
          currentIndex = i;
          break;
        }
      }

      var isMaxTier = currentIndex === variants.length - 1;
      var isMinTier = currentIndex === 0;

      // INCREASING
      if (delta > 0) {
        if (isMaxTier) {
          // Already at highest tier — just increase quantity
          return changeItem(lineItemKey, currentQty + 1);
        }
        // Swap up to next tier
        var upVariant = variants[currentIndex + 1].id;
        return shopifyFetch('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
        }).then(function () {
          return shopifyFetch('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({ items: [{ id: upVariant, quantity: 1 }] }),
          });
        }).then(function () {
          return fetchCart();
        }).then(function (cart) {
          renderDrawer(cart);
          return cart;
        });
      }

      // DECREASING
      if (delta < 0) {
        if (currentQty > 1) {
          // Multiple of same tier — just reduce quantity
          return changeItem(lineItemKey, currentQty - 1);
        }
        if (isMinTier) {
          // At lowest tier with qty 1 — remove
          return removeItem(lineItemKey);
        }
        // Swap down to previous tier
        var downVariant = variants[currentIndex - 1].id;
        return shopifyFetch('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: lineItemKey, quantity: 0 }),
        }).then(function () {
          return shopifyFetch('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({ items: [{ id: downVariant, quantity: 1 }] }),
          });
        }).then(function () {
          return fetchCart();
        }).then(function (cart) {
          renderDrawer(cart);
          return cart;
        });
      }

      return Promise.resolve(cartState);
    });
  }

  // ---------- Nav counter ----------

  function updateNavCounter() {
    if (!cartState) return;
    var count = cartState.item_count;
    document.querySelectorAll('.cart-count').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ---------- Money formatting ----------

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  // ---------- Drawer open / close ----------

  function openDrawer() {
    if (!drawerEl) bindDrawer();
    fetchCart().then(function (cart) {
      renderDrawer(cart);
      drawerEl.offsetHeight; // force reflow
      drawerEl.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove('open');
    document.body.style.overflow = '';
  }

  // ---------- Bind to server-rendered drawer ----------

  function bindDrawer() {
    drawerEl = document.getElementById('cartDrawer');
    if (!drawerEl) return;

    // Close handlers — overlay and X button
    drawerEl.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerEl && drawerEl.classList.contains('open')) {
        closeDrawer();
      }
    });
  }

  // ---------- Upsell logic ----------

  // Upsell config: if customer has tier X, suggest tier Y
  var upsellMap = {
    'Trial Protocol': {
      targetTitle: 'Core Protocol',
      badges: ['SAVE 18%', 'MOST POPULAR'],
      headline: 'Upgrade to the Core Protocol',
      reason: '<strong>1 month isn\u2019t enough for visible regrowth.</strong> Clinical data shows follicle reactivation begins at month 2\u20133. The Core Protocol covers the full response window.',
      timeline: [
        { label: 'Month 1', desc: 'Less shedding', tag: '\u2190 You\u2019re here', style: 'current' },
        { label: 'Month 2\u20133', desc: 'Follicles reactivate', tag: '', style: 'results' },
        { label: 'Month 3+', desc: 'Visible growth', tag: '', style: 'results' }
      ],
      mathOld: '$69/mo \u00d7 3 = $207',
      mathNew: '$169',
      savings: 'You save $38',
      ctaLabel: 'Upgrade to Core Protocol',
      ctaPrice: '$13/week',
      dismiss: 'No thanks, I\u2019ll stick with the Trial'
    },
    'Starter Protocol': {
      targetTitle: 'Full Transformation Protocol',
      badges: ['SAVE 47%', 'BEST VALUE'],
      headline: 'Upgrade to the Full Transformation Protocol',
      reason: '<strong>You\u2019re committed \u2014 go the full distance.</strong> The complete 6-month cycle delivers maximum density at the lowest cost per week.',
      timeline: [
        { label: 'Month 1\u20133', desc: 'Foundation phase', tag: '\u2190 You\u2019re here', style: 'current' },
        { label: 'Month 3\u20134', desc: '+17% new growth', tag: '', style: 'results' },
        { label: 'Month 6+', desc: 'Full density', tag: '', style: 'results' }
      ],
      mathOld: '$69/mo \u00d7 6 = $414',
      mathNew: '$219',
      savings: 'You save $195',
      ctaLabel: 'Upgrade to Full Transformation',
      ctaPrice: '$9/week',
      dismiss: 'No thanks, I\u2019ll stick with the Core'
    },
    'Core Protocol': {
      targetTitle: 'Full Transformation Protocol',
      badges: ['SAVE 47%', 'BEST VALUE'],
      headline: 'Upgrade to the Full Transformation Protocol',
      reason: '<strong>You\u2019re committed \u2014 go the full distance.</strong> The complete 6-month cycle delivers maximum density at the lowest cost per week.',
      timeline: [
        { label: 'Month 1\u20133', desc: 'Foundation phase', tag: '\u2190 You\u2019re here', style: 'current' },
        { label: 'Month 3\u20134', desc: '+17% new growth', tag: '', style: 'results' },
        { label: 'Month 6+', desc: 'Full density', tag: '', style: 'results' }
      ],
      mathOld: '$69/mo \u00d7 6 = $414',
      mathNew: '$219',
      savings: 'You save $195',
      ctaLabel: 'Upgrade to Full Transformation',
      ctaPrice: '$9/week',
      dismiss: 'No thanks, I\u2019ll stick with the Core'
    }
  };

  function buildUpsellCard(cart) {
    // Find the first item that has an upsell path
    var upsellItem = null;
    var upsellConfig = null;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.variant_title && upsellMap[item.variant_title]) {
        upsellItem = item;
        upsellConfig = upsellMap[item.variant_title];
        break;
      }
    }
    if (!upsellConfig || !upsellItem) return '';

    // Build timeline steps
    var timelineHtml = upsellConfig.timeline.map(function (step) {
      return (
        '<div class="cart-upsell__timeline-step cart-upsell__timeline-step--' + step.style + '">' +
          '<span class="cart-upsell__timeline-label">' + step.label + '</span>' +
          '<span class="cart-upsell__timeline-desc">' + step.desc + '</span>' +
          (step.tag ? '<span class="cart-upsell__timeline-you">' + step.tag + '</span>' : '') +
        '</div>'
      );
    }).join('');

    // Build badges
    var badgesHtml = upsellConfig.badges.map(function (b, idx) {
      var cls = idx === 0 ? 'cart-upsell__badge--save' : 'cart-upsell__badge--popular';
      return '<span class="cart-upsell__badge ' + cls + '">' + b + '</span>';
    }).join('');

    return (
      '<div class="cart-upsell" data-upsell-handle="' + upsellItem.handle + '" data-upsell-target="' + upsellConfig.targetTitle + '" data-upsell-line-key="' + upsellItem.key + '">' +
        '<div class="cart-upsell__badge-row">' + badgesHtml + '</div>' +
        '<p class="cart-upsell__title">' + upsellConfig.headline + '</p>' +
        '<p class="cart-upsell__reason">' + upsellConfig.reason + '</p>' +
        '<div class="cart-upsell__timeline">' + timelineHtml + '</div>' +
        '<div class="cart-upsell__math">' +
          '<span class="cart-upsell__math-left"><s>' + upsellConfig.mathOld + '</s> \u2192 <strong>' + upsellConfig.mathNew + '</strong></span>' +
          '<span class="cart-upsell__math-right">' + upsellConfig.savings + '</span>' +
        '</div>' +
        '<button class="cart-upsell__cta" data-upsell-upgrade>' +
          upsellConfig.ctaLabel + ' \u2014 <span class="cart-upsell__cta-price">' + upsellConfig.ctaPrice + '</span>' +
        '</button>' +
        '<div class="cart-upsell__dismiss"><a data-upsell-dismiss>' + upsellConfig.dismiss + '</a></div>' +
      '</div>'
    );
  }

  function handleUpsellUpgrade(upsellEl) {
    var handle = upsellEl.getAttribute('data-upsell-handle');
    var targetTitle = upsellEl.getAttribute('data-upsell-target');
    var lineKey = upsellEl.getAttribute('data-upsell-line-key');

    var ctaBtn = upsellEl.querySelector('[data-upsell-upgrade]');
    if (ctaBtn) {
      ctaBtn.textContent = 'Upgrading...';
      ctaBtn.style.opacity = '0.6';
    }

    // Fetch product variants to find the target
    fetchProductVariants(handle).then(function (variants) {
      var targetVariant = variants.find(function (v) { return v.title === targetTitle; });
      if (!targetVariant) return;

      // Remove current item, add upgrade
      return shopifyFetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      }).then(function () {
        return shopifyFetch('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: targetVariant.id, quantity: 1 }] })
        });
      }).then(function () {
        return fetchCart();
      }).then(function (cart) {
        renderDrawer(cart);
      });
    });
  }

  // ---------- Render drawer contents ----------

  function renderDrawer(cart) {
    if (!drawerEl) bindDrawer();
    if (!drawerEl || !cart) return;

    var body = document.getElementById('cartDrawerBody');
    var footer = document.getElementById('cartDrawerFooter');
    if (!body || !footer) return;

    // Free shipping threshold — check data attribute or use default
    var threshold = FREE_SHIPPING_THRESHOLD;
    var thresholdAttr = drawerEl.getAttribute('data-free-shipping-threshold');
    if (thresholdAttr) {
      threshold = parseInt(thresholdAttr, 10);
    }

    // Shipping progress bar — always update regardless of cart state
    var shippingBar = document.getElementById('cartShippingBar');
    if (shippingBar) {
      if (cart.items.length === 0) {
        shippingBar.style.display = 'none';
        shippingBar.innerHTML = '';
      } else if (cart.total_price >= threshold) {
        shippingBar.style.display = '';
        shippingBar.innerHTML =
          '<p class="cart-shipping-bar__text cart-shipping-bar__text--success">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2755C4" stroke-width="2" stroke-linecap="round"><path d="M20 6L9 17l-5-5"/></svg>' +
            'Congrats, you have unlocked <strong>free shipping!</strong>' +
          '</p>' +
          '<div class="cart-shipping-bar__track"><div class="cart-shipping-bar__fill" style="width:100%"></div></div>';
      } else {
        var remaining = threshold - cart.total_price;
        var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
        shippingBar.style.display = '';
        shippingBar.innerHTML =
          '<p class="cart-shipping-bar__text">You\u2019re <strong>' + formatMoney(remaining) + '</strong> away from free shipping!</p>' +
          '<div class="cart-shipping-bar__track"><div class="cart-shipping-bar__fill" style="width:' + pct + '%"></div></div>';
      }
    }

    if (cart.items.length === 0) {
      body.innerHTML =
        '<div class="cart-drawer__empty">' +
          '<p>Your cart is empty</p>' +
          '<a href="/collections/all" class="btn btn--secondary" style="margin-top: var(--space-lg);">Shop Now</a>' +
        '</div>';
      footer.innerHTML = '';
      return;
    }

    // Render items
    var itemsHtml = cart.items.map(function (item) {
      var imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/(\.\w+)(\?|$)/, '_120x$1$2')
        : item.image
          ? item.image.replace(/(\.\w+)(\?|$)/, '_120x$1$2')
          : '';
      var imgHtml = imgSrc
        ? '<img class="cart-drawer__item-img" src="' + imgSrc + '" alt="' + item.product_title + '" width="80" height="80" loading="lazy">'
        : '';
      var variantHtml = item.variant_title
        ? '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>'
        : '';
      return (
        '<div class="cart-drawer__item" data-line-key="' + item.key + '">' +
          imgHtml +
          '<div class="cart-drawer__item-info">' +
            '<p class="cart-drawer__item-name">' + item.product_title + '</p>' +
            variantHtml +
            (item.properties && Object.keys(item.properties).length > 0
              ? '<p class="cart-drawer__item-detail">' +
                  Object.entries(item.properties).map(function (kv) { return kv[0] + ': ' + kv[1]; }).join(' · ') +
                '</p>'
              : '') +
            '<p class="cart-drawer__item-price font-data">' + formatMoney(item.final_line_price) + '</p>' +
          '</div>' +
          '<div class="cart-drawer__item-actions">' +
            '<div class="cart-drawer__qty">' +
              '<button class="cart-drawer__qty-btn" data-qty-change data-line-key="' + item.key + '" data-delta="-1" data-handle="' + item.handle + '" data-variant-id="' + item.variant_id + '" aria-label="Decrease quantity">\u2212</button>' +
              '<span class="cart-drawer__qty-value">' + item.quantity + '</span>' +
              '<button class="cart-drawer__qty-btn" data-qty-change data-line-key="' + item.key + '" data-delta="1" data-handle="' + item.handle + '" data-variant-id="' + item.variant_id + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button class="cart-drawer__remove" data-remove-key="' + item.key + '" aria-label="Remove item">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>' +
              '</svg>' +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    // Build upsell card if applicable
    var upsellHtml = buildUpsellCard(cart);

    body.innerHTML = itemsHtml + upsellHtml +
      '<div class="cart-timeline">' +
        '<div class="cart-timeline__header">' +
          '<p class="cart-timeline__badge">CLINICALLY BACKED PROTOCOL</p>' +
          '<h3 class="cart-timeline__title">What to <em>Actually</em> Expect</h3>' +
          '<p class="cart-timeline__sub">Based on published clinical data at the exact concentrations in our formula.</p>' +
        '</div>' +
        '<div class="cart-timeline__steps">' +
          '<div class="cart-timeline__step">' +
            '<span class="cart-timeline__dot cart-timeline__dot--active"></span>' +
            '<div>' +
              '<p class="cart-timeline__period">Week 1\u20134</p>' +
              '<p class="cart-timeline__desc">Reduced shedding. Scalp feels healthier. Microchannels improve absorption 12.5\u00d7.</p>' +
            '</div>' +
          '</div>' +
          '<div class="cart-timeline__step">' +
            '<span class="cart-timeline__dot cart-timeline__dot--active"></span>' +
            '<div>' +
              '<p class="cart-timeline__period">Month 2\u20133</p>' +
              '<p class="cart-timeline__desc">Follicle stem cells reactivating. Vellus hairs may appear. DHT modulation at follicle.</p>' +
            '</div>' +
          '</div>' +
          '<div class="cart-timeline__step">' +
            '<span class="cart-timeline__dot"></span>' +
            '<div>' +
              '<p class="cart-timeline__period">Month 3\u20134</p>' +
              '<p class="cart-timeline__desc">Visible new growth. +17% anagen hairs (Redensyl 84-day data). Hair shaft thickening.</p>' +
            '</div>' +
          '</div>' +
          '<div class="cart-timeline__step">' +
            '<span class="cart-timeline__dot"></span>' +
            '<div>' +
              '<p class="cart-timeline__period">Month 6+</p>' +
              '<p class="cart-timeline__desc">Full density improvement. +121% growth rate (Procapil). Terminal hair conversion.</p>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="cart-timeline__stats">' +
          '<div class="cart-timeline__stat"><span class="cart-timeline__stat-num">+17%</span><span class="cart-timeline__stat-label">Anagen hairs<br>in 84 days</span></div>' +
          '<div class="cart-timeline__stat"><span class="cart-timeline__stat-num">+121%</span><span class="cart-timeline__stat-label">Growth rate<br>in 4 months</span></div>' +
          '<div class="cart-timeline__stat"><span class="cart-timeline__stat-num">6</span><span class="cart-timeline__stat-label">Biological<br>pathways</span></div>' +
        '</div>' +
      '</div>';

    // Shipping message
    var shippingMsg = cart.total_price >= threshold
      ? '<p class="cart-drawer__shipping">Free shipping included</p>'
      : '<p class="cart-drawer__shipping">Free shipping on orders over ' + formatMoney(threshold) + '</p>';

    // Item count label
    var totalItems = cart.items.reduce(function (sum, i) { return sum + i.quantity; }, 0);
    var itemLabel = totalItems === 1 ? '1 Item' : totalItems + ' Items';

    footer.innerHTML =
      '<div class="cart-drawer__subtotal">' +
        '<div><strong>Subtotal:</strong> <span style="color:var(--color-text-secondary);font-size:0.8125rem;">' + itemLabel + '</span></div>' +
        '<span class="font-data" style="font-size:1.125rem;">' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      shippingMsg +
      '<button class="btn btn--primary btn--lg cart-drawer__checkout" style="width:100%;margin-top:var(--space-md);background:linear-gradient(135deg,#1f44a0 0%,#5577d0 100%);border:none;border-radius:8px;padding:16px;font-size:0.875rem;font-weight:600;text-transform:uppercase;letter-spacing:0.1em;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;">Proceed to Checkout <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></button>' +
      '<div class="cart-drawer__trust">' +
        '<div class="cart-drawer__guarantee">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2755C4" stroke-width="1.5" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>' +
          '<span>90-Day Money-Back Guarantee</span>' +
        '</div>' +
        '<div class="cart-drawer__rating">' +
          '<span style="font-weight:600;color:var(--color-text-primary);">4.8/5</span>' +
          '<span style="color:#00B67A;letter-spacing:-1px;">★★★★★</span>' +
          '<span style="color:var(--color-text-secondary);">247 reviews</span>' +
        '</div>' +
      '</div>' +
      '<button class="cart-drawer__continue" data-cart-close>Continue Shopping</button>';

    // Wire quantity buttons — swap variant tiers instead of changing qty
    body.querySelectorAll('[data-qty-change]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-line-key');
        var delta = parseInt(btn.getAttribute('data-delta'), 10);
        var handle = btn.getAttribute('data-handle');
        var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);

        if (handle && variantId) {
          // Use tier swapping — move up/down the variant ladder
          var cartItem = cart.items.find(function (i) { return i.key === key; });
          var qty = cartItem ? cartItem.quantity : 1;
          swapVariantTier(key, variantId, handle, delta, qty);
        } else {
          // Fallback: standard qty change for non-tiered products
          var item = cart.items.find(function (i) { return i.key === key; });
          if (item) {
            var newQty = item.quantity + delta;
            if (newQty < 1) {
              removeItem(key);
            } else {
              changeItem(key, newQty);
            }
          }
        }
      });
    });

    // Wire remove buttons
    body.querySelectorAll('[data-remove-key]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeItem(btn.getAttribute('data-remove-key'));
      });
    });

    // Wire upsell upgrade button
    var upsellEl = body.querySelector('.cart-upsell');
    if (upsellEl) {
      var upgradeBtn = upsellEl.querySelector('[data-upsell-upgrade]');
      if (upgradeBtn) {
        upgradeBtn.addEventListener('click', function () {
          handleUpsellUpgrade(upsellEl);
        });
      }
      var dismissBtn = upsellEl.querySelector('[data-upsell-dismiss]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          upsellEl.style.display = 'none';
        });
      }
    }

    // Wire checkout button
    var checkoutBtn = footer.querySelector('.cart-drawer__checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function () {
        window.location.href = '/checkout';
      });
    }

    // Wire continue shopping
    footer.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
  }

  // ---------- Init ----------

  function init() {
    bindDrawer();
    fetchCart();

    // Make nav cart links open drawer
    document.querySelectorAll('[data-cart-toggle]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer();
      });
    });
  }

  // Auto-init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public interface
  return {
    addItem: addItem,
    removeItem: removeItem,
    changeItem: changeItem,
    fetchCart: fetchCart,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
  };
})();
